"use client";

import styled from "@emotion/styled";
import Image from "next/image";
import ReactMarkdown, { Options, Components } from "react-markdown";
import toHtmlId from "../utils/to-html-id";

// https://amirardalan.com/blog/use-next-image-with-react-markdown
const components: Components = {
  h1: ({ children }) => <h2 id={toHtmlId(children)}>{children}</h2>,
  h2: ({ children }) => <h2 id={toHtmlId(children)}>{children}</h2>,
  h3: ({ children }) => <h3 id={toHtmlId(children)}>{children}</h3>,
  h4: ({ children }) => <h4 id={toHtmlId(children)}>{children}</h4>,
  p: props => {
    const { node } = props;

    // https://github.com/remarkjs/react-markdown/issues/711
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const child: any = node?.children[0];

    if (child.value === "---") {
      return <hr />;
    }

    if (child?.tagName === "img") {
      const metastring = child.properties.alt;
      const alt = metastring?.replace(/ *\{[^)]*\} */g, "");
      const metaWidth = metastring.match(/{([^}]+)x/);
      const metaHeight = metastring.match(/x([^}]+)}/);
      const width = metaWidth ? metaWidth[1] : "600";
      const height = metaHeight ? metaHeight[1] : "400";

      return (
        <div>
          <Image
            src={child.properties.src}
            width={width}
            height={height}
            alt={alt}
            style={{
              maxWidth: "100%",
              height: "auto",
            }}
          />
        </div>
      );
    }

    return <p>{props.children}</p>;
  },
};

const CustomReactMarkdown: React.FC<Options> = props => (
  <ReactMarkdown
    {...props}
    components={{
      ...components,
      ...(props.components ?? {}),
    }}
  />
);

const Markdown = styled(CustomReactMarkdown)`
  * + * {
    margin-top: 1em;
  }

  li + li {
    margin-top: 0.5em;
  }

  hr {
    margin: 3rem 0;
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: bold;
  }

  h1,
  h2 {
    font-size: 1.5em;
  }

  @media screen and (min-width: 48em) {
    h2 {
      width: 90%;
    }
  }

  h3 {
    font-size: 1.3em;
  }

  h4 {
    font-size: 1.15em;
  }

  h5 {
    font-size: 1em;
  }

  li {
    margin-bottom: 0;
    word-break: break-word;
  }

  ul {
    list-style: disc;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
  }

  ol {
    list-style: decimal;
  }

  ul,
  ol {
    margin-left: 1em;
  }

  ul ul {
    list-style-type: circle;
  }

  ul ul,
  ul ol,
  ol ol,
  ol ul {
    margin-left: 2em;
  }

  a {
    color: #2b504a;
    text-decoration: underline;
    text-decoration-thickness: 2px;
    word-break: break-word;
  }

  img {
    margin: 2rem auto;
  }
`;

export default Markdown;
